import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StatusToggle from '../../../Shared/StatusToggle';
import * as adminHandlers from '../../../../routes/Admin/redux/handlers';

const VendorManagement = ({ brandId, subBrandId, isEditMode }) => {
    const dispatch = useDispatch();
    const { vendors, vendorsLoading, vendorsTotal } = useSelector(state => state.admin);
    const [showAddVendor, setShowAddVendor] = useState(false);
    const [editingVendor, setEditingVendor] = useState(null);
    const [newVendor, setNewVendor] = useState({
        name: '',
        sku: '',
        type: 'SLAB',
        min: 100,
        max: 10000,
        denominations: [],
        discount: 0,
        cashback: 0,
        is_active: true
    });
    const [denominationInput, setDenominationInput] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (brandId && subBrandId) {
            fetchVendors();
        }
    }, [brandId, subBrandId]);

    const fetchVendors = async () => {
        try {
            await dispatch(adminHandlers.fetchSubBrandVendorsHandler(brandId, subBrandId));
        } catch (error) {
            console.error('Failed to fetch vendors:', error);
        }
    };

    const handleAddDenomination = () => {
        const value = denominationInput.trim();
        if (value && !newVendor.denominations.includes(value)) {
            setNewVendor(prev => ({
                ...prev,
                denominations: [...prev.denominations, value].sort((a, b) => parseInt(a) - parseInt(b))
            }));
            setDenominationInput('');
        }
    };

    const handleRemoveDenomination = (value) => {
        setNewVendor(prev => ({
            ...prev,
            denominations: prev.denominations.filter(d => d !== value)
        }));
    };

    const validateVendor = (vendor) => {
        const errors = {};
        
        if (!vendor.name?.trim()) {
            errors.name = 'Vendor name is required';
        }
        
        if (!vendor.sku?.trim()) {
            errors.sku = 'SKU is required';
        }
        
        if (vendor.min && vendor.max && parseFloat(vendor.min) > parseFloat(vendor.max)) {
            errors.range = 'Min value cannot be greater than max value';
        }
        
        if (vendor.type === 'SLAB' && (!vendor.denominations || vendor.denominations.length === 0)) {
            errors.denominations = 'At least one denomination is required for SLAB type';
        }
        
        return errors;
    };

    const handleSaveVendor = async () => {
        const vendorData = editingVendor || newVendor;
        const validationErrors = validateVendor(vendorData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            
            // Prepare vendor data for API
            const apiData = {
                name: vendorData.name,
                sku: vendorData.sku,
                type: vendorData.type,
                min: parseFloat(vendorData.min),
                max: parseFloat(vendorData.max),
                denominations: vendorData.type === 'SLAB' ? vendorData.denominations : null,
                discount: parseFloat(vendorData.discount) || 0,
                cashback: parseFloat(vendorData.cashback) || 0,
                is_active: vendorData.is_active
            };

            if (editingVendor && editingVendor.id) {
                await dispatch(adminHandlers.updateSubBrandVendorHandler(
                    brandId, 
                    subBrandId, 
                    editingVendor.id, 
                    apiData
                ));
            } else {
                await dispatch(adminHandlers.createSubBrandVendorHandler(
                    brandId, 
                    subBrandId, 
                    apiData
                ));
            }

            // Reset forms
            setShowAddVendor(false);
            setEditingVendor(null);
            setNewVendor({
                name: '',
                sku: '',
                type: 'SLAB',
                min: 100,
                max: 10000,
                denominations: [],
                discount: 0,
                cashback: 0,
                is_active: true
            });
            setErrors({});
            
            // Refresh vendors list
            await fetchVendors();
        } catch (error) {
            console.error('Failed to save vendor:', error);
            setErrors({ save: 'Failed to save vendor. Please try again.' });
        }
    };


    const handleEditVendor = (vendor) => {
        console.log('Editing vendor:', vendor);
        console.log('Vendor type:', vendor.type);
        console.log('Vendor denominations:', vendor.denominations);
        
        // Normalize the type to uppercase to handle any case inconsistencies
        const normalizedType = vendor.type ? vendor.type.toUpperCase() : 'SLAB';
        
        setEditingVendor({
            ...vendor,
            type: normalizedType,
            denominations: vendor.denominations || []
        });
        setShowAddVendor(false);
        setErrors({});
        setDenominationInput(''); // Clear the input field
    };

    const handleCancelEdit = () => {
        setEditingVendor(null);
        setShowAddVendor(false);
        setNewVendor({
            name: '',
            sku: '',
            type: 'SLAB',
            min: 100,
            max: 10000,
            denominations: [],
            discount: 0,
            cashback: 0,
            is_active: true
        });
        setErrors({});
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">Vendor Management</h3>
                {isEditMode && !showAddVendor && !editingVendor && (
                    <button
                        onClick={() => setShowAddVendor(true)}
                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                    >
                        Add Vendor
                    </button>
                )}
            </div>

            {vendorsLoading ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                </div>
            ) : (
                <>
                    {/* Add New Vendor Form (only shows when adding, not editing) */}
                    {showAddVendor && (
                        <div className="bg-charleston-green border border-zinc-700 rounded-lg p-4 space-y-4">
                            <h4 className="text-white font-medium">New Vendor</h4>

                            {/* Name and SKU */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">Name</label>
                                    <input
                                        type="text"
                                        value={newVendor.name}
                                        onChange={(e) => {
                                            setNewVendor(prev => ({ ...prev, name: e.target.value }));
                                            setErrors(prev => ({ ...prev, name: undefined }));
                                        }}
                                        className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                            errors.name ? 'border-red-500' : 'border-zinc-700'
                                        } focus:border-blue-500 focus:outline-none`}
                                    />
                                    {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                </div>
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">SKU</label>
                                    <input
                                        type="text"
                                        value={newVendor.sku}
                                        onChange={(e) => {
                                            setNewVendor(prev => ({ ...prev, sku: e.target.value }));
                                            setErrors(prev => ({ ...prev, sku: undefined }));
                                        }}
                                        className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                            errors.sku ? 'border-red-500' : 'border-zinc-700'
                                        } focus:border-blue-500 focus:outline-none`}
                                    />
                                    {errors.sku && <p className="text-red-500 text-xs mt-1">{errors.sku}</p>}
                                </div>
                            </div>

                            {/* Type */}
                            <div>
                                <label className="text-xs text-zinc-400 block mb-1">Type</label>
                                <select
                                    value={newVendor.type}
                                    onChange={(e) => {
                                        setNewVendor(prev => ({ ...prev, type: e.target.value }));
                                    }}
                                    className="w-full bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                >
                                    <option value="SLAB">Slab Based</option>
                                    <option value="RANGE">Range Based</option>
                                </select>
                            </div>

                            {/* Min and Max */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">Min Value</label>
                                    <input
                                        type="number"
                                        value={newVendor.min}
                                        onChange={(e) => {
                                            setNewVendor(prev => ({ ...prev, min: e.target.value }));
                                            setErrors(prev => ({ ...prev, range: undefined }));
                                        }}
                                        className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                            errors.range ? 'border-red-500' : 'border-zinc-700'
                                        } focus:border-blue-500 focus:outline-none`}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">Max Value</label>
                                    <input
                                        type="number"
                                        value={newVendor.max}
                                        onChange={(e) => {
                                            setNewVendor(prev => ({ ...prev, max: e.target.value }));
                                            setErrors(prev => ({ ...prev, range: undefined }));
                                        }}
                                        className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                            errors.range ? 'border-red-500' : 'border-zinc-700'
                                        } focus:border-blue-500 focus:outline-none`}
                                    />
                                </div>
                            </div>
                            {errors.range && <p className="text-red-500 text-xs">{errors.range}</p>}

                            {/* Slabs/Denominations (for FIXED/SLAB type) */}
                            {newVendor.type === 'SLAB' && (
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">Slab Values</label>
                                    <div className="flex gap-2 mb-2">
                                        <input
                                            type="number"
                                            value={denominationInput}
                                            onChange={(e) => setDenominationInput(e.target.value)}
                                            placeholder="Enter slab value (e.g., 100, 1000)"
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddDenomination();
                                                }
                                            }}
                                            className="flex-1 bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                        />
                                        <button
                                            type="button"
                                            onClick={handleAddDenomination}
                                            className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                        >
                                            Add
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {newVendor.denominations.map((denom) => (
                                            <span
                                                key={denom}
                                                className="inline-flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-600 text-white rounded-full text-sm"
                                            >
                                                ₹{denom}
                                                <button
                                                    onClick={() => handleRemoveDenomination(denom)}
                                                    className="ml-1 text-zinc-400 hover:text-red-500"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                    {errors.denominations && <p className="text-red-500 text-xs mt-1">{errors.denominations}</p>}
                                </div>
                            )}

                            {/* Discount and Cashback */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">Discount (%)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={newVendor.discount}
                                        onChange={(e) => {
                                            setNewVendor(prev => ({ ...prev, discount: e.target.value }));
                                        }}
                                        className="w-full bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-zinc-400 block mb-1">Cashback (%)</label>
                                    <input
                                        type="number"
                                        step="0.1"
                                        value={newVendor.cashback}
                                        onChange={(e) => {
                                            setNewVendor(prev => ({ ...prev, cashback: e.target.value }));
                                        }}
                                        className="w-full bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Active Status */}
                            <div>
                                <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Status</p>
                                <StatusToggle
                                    checked={!!newVendor.is_active}
                                    onChange={(v) => setNewVendor(prev => ({ ...prev, is_active: v }))}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm hover:bg-zinc-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSaveVendor}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                >
                                    Create Vendor
                                </button>
                            </div>

                            {errors.save && (
                                <div className="bg-red-950 border border-red-800 rounded-lg p-2">
                                    <p className="text-red-400 text-xs">{errors.save}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Vendors List */}
                    {vendors.length > 0 ? (
                        <div className="space-y-3">
                            {vendors.map((vendor) => (
                                // Show edit form in place of card when editing this specific vendor
                                editingVendor && editingVendor.id === vendor.id ? (
                                    <div key={vendor.id} className="bg-charleston-green border border-zinc-700 rounded-lg p-4 space-y-4">
                                        <h4 className="text-white font-medium">Edit Vendor</h4>

                                        {/* Name and SKU */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">Name</label>
                                                <input
                                                    type="text"
                                                    value={editingVendor.name}
                                                    onChange={(e) => {
                                                        setEditingVendor(prev => ({ ...prev, name: e.target.value }));
                                                        setErrors(prev => ({ ...prev, name: undefined }));
                                                    }}
                                                    className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                                        errors.name ? 'border-red-500' : 'border-zinc-700'
                                                    } focus:border-blue-500 focus:outline-none`}
                                                />
                                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                                            </div>
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">SKU</label>
                                                <input
                                                    type="text"
                                                    value={editingVendor.sku}
                                                    onChange={(e) => {
                                                        setEditingVendor(prev => ({ ...prev, sku: e.target.value }));
                                                        setErrors(prev => ({ ...prev, sku: undefined }));
                                                    }}
                                                    className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                                        errors.sku ? 'border-red-500' : 'border-zinc-700'
                                                    } focus:border-blue-500 focus:outline-none`}
                                                />
                                                {errors.sku && <p className="text-red-500 text-xs mt-1">{errors.sku}</p>}
                                            </div>
                                        </div>

                                        {/* Type */}
                                        <div>
                                            <label className="text-xs text-zinc-400 block mb-1">Type</label>
                                            <select
                                                value={editingVendor.type}
                                                onChange={(e) => {
                                                    console.log('Type select changed to:', e.target.value);
                                                    setEditingVendor(prev => ({ ...prev, type: e.target.value }));
                                                }}
                                                className="w-full bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                            >
                                                <option value="SLAB">Slab Based</option>
                                                <option value="RANGE">Range Based</option>
                                            </select>
                                        </div>

                                        {/* Min and Max */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">Min Value</label>
                                                <input
                                                    type="number"
                                                    value={editingVendor.min}
                                                    onChange={(e) => {
                                                        setEditingVendor(prev => ({ ...prev, min: e.target.value }));
                                                        setErrors(prev => ({ ...prev, range: undefined }));
                                                    }}
                                                    className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                                        errors.range ? 'border-red-500' : 'border-zinc-700'
                                                    } focus:border-blue-500 focus:outline-none`}
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">Max Value</label>
                                                <input
                                                    type="number"
                                                    value={editingVendor.max}
                                                    onChange={(e) => {
                                                        setEditingVendor(prev => ({ ...prev, max: e.target.value }));
                                                        setErrors(prev => ({ ...prev, range: undefined }));
                                                    }}
                                                    className={`w-full bg-eerie-black border rounded-lg px-3 py-2 text-white text-sm ${
                                                        errors.range ? 'border-red-500' : 'border-zinc-700'
                                                    } focus:border-blue-500 focus:outline-none`}
                                                />
                                            </div>
                                        </div>
                                        {errors.range && <p className="text-red-500 text-xs">{errors.range}</p>}

                                        {/* Discount and Cashback */}
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">Discount (%)</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={editingVendor.discount}
                                                    onChange={(e) => setEditingVendor(prev => ({ ...prev, discount: e.target.value }))}
                                                    className="w-full bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">Cashback (%)</label>
                                                <input
                                                    type="number"
                                                    step="0.1"
                                                    value={editingVendor.cashback}
                                                    onChange={(e) => setEditingVendor(prev => ({ ...prev, cashback: e.target.value }))}
                                                    className="w-full bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                                />
                                            </div>
                                        </div>

                                        {/* Slabs/Denominations (for FIXED/SLAB type) */}
                                        {console.log('Edit form - editingVendor:', editingVendor)}
                                        {console.log('Edit form - editingVendor.type:', editingVendor?.type)}
                                        {console.log('Edit form - type check:', editingVendor?.type === 'SLAB')}
                                        {editingVendor && editingVendor.type === 'SLAB' && (
                                            <div>
                                                <label className="text-xs text-zinc-400 block mb-1">Slab Values</label>
                                                <div className="flex gap-2 mb-2">
                                                    <input
                                                        type="number"
                                                        value={denominationInput}
                                                        onChange={(e) => setDenominationInput(e.target.value)}
                                                        placeholder="Enter slab value (e.g., 100, 1000)"
                                                        onKeyPress={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                const value = denominationInput.trim();
                                                                if (value && !editingVendor.denominations.includes(value)) {
                                                                    setEditingVendor(prev => ({
                                                                        ...prev,
                                                                        denominations: [...prev.denominations, value].sort((a, b) => parseInt(a) - parseInt(b))
                                                                    }));
                                                                    setDenominationInput('');
                                                                }
                                                            }
                                                        }}
                                                        className="flex-1 bg-eerie-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-500 focus:outline-none"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            const value = denominationInput.trim();
                                                            if (value && !editingVendor.denominations.includes(value)) {
                                                                setEditingVendor(prev => ({
                                                                    ...prev,
                                                                    denominations: [...prev.denominations, value].sort((a, b) => parseInt(a) - parseInt(b))
                                                                }));
                                                                setDenominationInput('');
                                                            }
                                                        }}
                                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                                <div className="flex flex-wrap gap-2">
                                                    {editingVendor.denominations && editingVendor.denominations.map((denom) => (
                                                        <span
                                                            key={denom}
                                                            className="inline-flex items-center gap-1 px-3 py-1 bg-zinc-800 border border-zinc-600 text-white rounded-full text-sm"
                                                        >
                                                            ₹{denom}
                                                            <button
                                                                type="button"
                                                                onClick={() => setEditingVendor(prev => ({
                                                                    ...prev,
                                                                    denominations: prev.denominations.filter(d => d !== denom)
                                                                }))}
                                                                className="ml-1 text-zinc-400 hover:text-red-500"
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                                </div>
                                                {errors.denominations && <p className="text-red-500 text-xs mt-1">{errors.denominations}</p>}
                                            </div>
                                        )}

                                        {/* Active Status */}
                                        <div>
                                            <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Status</p>
                                            <StatusToggle
                                                checked={!!editingVendor.is_active}
                                                onChange={(v) => setEditingVendor(prev => ({ ...prev, is_active: v }))}
                                            />
                                        </div>

                                        {/* Action Buttons */}
                                        <div className="flex gap-2 justify-end">
                                            <button
                                                onClick={handleCancelEdit}
                                                className="px-4 py-2 bg-zinc-700 text-white rounded-lg text-sm hover:bg-zinc-600 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                onClick={handleSaveVendor}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                            >
                                                Update Vendor
                                            </button>
                                        </div>

                                        {errors.save && (
                                            <div className="bg-red-950 border border-red-800 rounded-lg p-2">
                                                <p className="text-red-400 text-xs">{errors.save}</p>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Show vendor card when not editing
                                    <div
                                        key={vendor.id}
                                        className="bg-charleston-green border border-zinc-700 rounded-lg p-4"
                                    >
                                        <div className="flex flex-col">
                                            {/* Header row with name, active status, and edit button */}
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <h4 className="text-white font-medium">{vendor.name}</h4>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                                                        vendor.is_active 
                                                            ? 'bg-green-500/20 text-green-400' 
                                                            : 'bg-red-500/20 text-red-400'
                                                    }`}>
                                                        {vendor.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </div>
                                                
                                                {isEditMode && (
                                                    <button
                                                        onClick={() => handleEditVendor(vendor)}
                                                        className="p-2 text-blue-400 hover:text-blue-300 transition-colors"
                                                    >
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                                                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" 
                                                            />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                            
                                            {/* Details grid */}
                                            <div className="grid text-sm" style={{ gridTemplateColumns: '30% 15% 25% 15% 15%' }}>
                                                <div>
                                                    <p className="text-zinc-500 text-xs">SKU</p>
                                                    <p className="text-white">{vendor.sku}</p>
                                                </div>
                                                <div>
                                                    <p className="text-zinc-500 text-xs">Type</p>
                                                    <p className="text-white">{vendor.type === 'SLAB' ? 'SLAB' : vendor.type}</p>
                                                </div>
                                                <div>
                                                    <p className="text-zinc-500 text-xs">{vendor.type === 'SLAB' ? 'Slabs' : 'Range'}</p>
                                                    <p className="text-white">
                                                        ₹{vendor.min} - ₹{vendor.max}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-zinc-500 text-xs">Discount</p>
                                                    <p className="text-white">{vendor.discount}%</p>
                                                </div>
                                                <div>
                                                    <p className="text-zinc-500 text-xs">Cashback</p>
                                                    <p className="text-white">{vendor.cashback}%</p>
                                                </div>
                                            </div>

                                            {vendor.type === 'SLAB' && vendor.denominations && vendor.denominations.length > 0 && (
                                                <div className="mt-3">
                                                    <p className="text-zinc-500 text-xs mb-1">All Slab Values</p>
                                                    <div className="flex flex-wrap gap-1">
                                                        {vendor.denominations.map(denom => (
                                                            <span key={denom} className="px-2 py-0.5 bg-zinc-800 border border-zinc-600 text-white rounded text-xs">
                                                                ₹{denom}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                    ) : !showAddVendor && !editingVendor && (
                        <div className="bg-charleston-green border border-zinc-700 rounded-lg p-8 text-center">
                            <p className="text-zinc-400 mb-3">No vendors configured for this sub-brand</p>
                            {isEditMode && (
                                <button
                                    onClick={() => setShowAddVendor(true)}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors"
                                >
                                    Add First Vendor
                                </button>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VendorManagement;